import React from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import { useAuthStore } from '@/stores/authStore'
import { useEntrepreneurAccesses } from '@/hooks/useEntrepreneurAccesses'
import { UserRole } from '@/types/auth'
import { ePath } from '@/utils/paths'
import { carrying } from '@/lib/accountActivation'

/**
 * Gate for the participant side of Gaia. Staff go through `StaffRoute`, which
 * resolves a staff resource grant and 403s an ENTREPRENEUR; this is its B2C
 * twin. A participant passes only with an ACTIVE Gaia membership — a pending
 * one is sent to verify its email, and anyone without a Gaia membership at all
 * gets the same 403 screen the staff guard shows.
 */
export const GaiaParticipantRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation()
  const { user, isAuthenticated, isLoading } = useAuthStore()
  const { accesses, isLoading: accessesLoading } = useEntrepreneurAccesses()

  if (isLoading || accessesLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-foreground" />
      </div>
    )
  }

  if (!isAuthenticated || user?.role !== UserRole.ENTREPRENEUR) {
    window.location.href = carrying(ePath('/login'))
    return null
  }

  const gaiaAccess = accesses?.find((access) => access.platform === 'gaia')

  if (!gaiaAccess) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="mb-4 text-4xl font-bold text-destructive">403</h1>
          <p className="text-muted-foreground">
            {t(
              'gaia.participant.noAccess',
              'Tu cuenta no tiene acceso a Gaia.',
            )}
          </p>
        </div>
      </div>
    )
  }

  if (!gaiaAccess.is_active) {
    window.location.href = carrying(ePath('/verify-email'))
    return null
  }

  return <>{children}</>
}
